import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  AtSignIcon,
  LockIcon,
  ArrowLeftIcon,
  CameraIcon,
  PlusIcon,
  EyeIcon,
  EyeOffIcon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "@/hooks/usePageTracking";
import { Capacitor } from "@capacitor/core";
import PhotoSelectionModal from "../components/PhotoSelectionModal";

import { crashlytics } from "../services/firebase";
// Animated avatar options using DiceBear API with different styles
const AVATAR_OPTIONS = [
  { name: "Adventurer", style: "adventurer" },
  { name: "Avataaars", style: "avataaars" },
  { name: "Big Smile", style: "big-smile" },
  { name: "Bottts", style: "bottts" },
  { name: "Fun Emoji", style: "fun-emoji" },
  { name: "Lorelei", style: "lorelei" },
  { name: "Micah", style: "micah" },
  { name: "Miniavs", style: "miniavs" },
  { name: "Notionists", style: "notionists" },
  { name: "Open Peeps", style: "open-peeps" },
];

const SignUpPage: React.FC = () => {
  usePageTracking("SignUp", "SignUpPage");
  const navigate = useNavigate();
  const {
    signup,
    signupWithGoogle,
    signupWithApple,
    isAuthenticated,
    currentUser,
  } = useAuth();
  const { addToast } = useToast();
  const platform = Capacitor.getPlatform();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string>("");
  const [selectedAvatar, setSelectedAvatar] = useState<string>("miniavs");
  const [lockedAvatarSeed, setLockedAvatarSeed] = useState<string | null>(null);
  const [avatarSeedVersion, setAvatarSeedVersion] = useState(0);
  const [showAvatarSelection, setShowAvatarSelection] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hasClickedTerms, setHasClickedTerms] = useState(false);

  const avatarPreviewSeed = `${email.trim() || "preview"}-${avatarSeedVersion}`;

  // Handle successful authentication
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      setIsLoading(false);
      // Navigate based on onboarding status
      if (currentUser.hasCompletedOnboarding) {
        navigate("/", { replace: true });
      } else {
        navigate("/profile-builder", { replace: true });
      }
    }
  }, [isAuthenticated, currentUser, navigate]);

  // Remove uploaded photo
  const removePhoto = () => {
    setProfilePhotoUrl("");
  };

  const handleAvatarPhotoClick = async () => {
    setShowAvatarSelection(false);
    setIsPhotoModalOpen(true);
  };

  // Select avatar
  const selectAvatar = (style: string) => {
    setSelectedAvatar(style);
    setLockedAvatarSeed(avatarPreviewSeed);
    setProfilePhotoUrl("");
    setShowAvatarSelection(false);
  };

  const refreshAvatarChoices = () => {
    setAvatarSeedVersion((prev) => prev + 1);
  };

  // Get current profile image URL
  const getCurrentProfileImage = () => {
    if (profilePhotoUrl) return profilePhotoUrl;
    const seed = lockedAvatarSeed || avatarPreviewSeed;
    return `https://api.dicebear.com/8.x/${selectedAvatar}/svg?seed=${encodeURIComponent(seed)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (!acceptedTerms) {
      setError("Please accept the Terms and Conditions to continue.");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // Determine profile image URL
      let finalProfileImageUrl = getCurrentProfileImage();

      await signup(email, password, finalProfileImageUrl);
      addToast("Account created successfully!", "success");
      // Keep loading until auth state changes and navigation happens
      // setIsLoading will be set to false in the useEffect when auth state updates
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred.",
      );
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signupWithGoogle();
    } catch (err: any) {
      crashlytics.logError(
        `Error signing up with Google: ${JSON.stringify(err?.message)}`,
        "SignUpPage.tsx",
        err,
      );
      if (
        err.message.includes("popup-closed-by-user") ||
        err.message.includes("cancelled")
      ) {
        setError("Sign up was cancelled. Please try again.");
      } else {
        setError(
          err.message || "Failed to sign up with Google. Please try again.",
        );
      }
      setIsLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signupWithApple();
    } catch (err: any) {
      crashlytics.logError(
        `Error signing up with Apple: ${JSON.stringify(err?.message)}`,
        "SignUpPage.tsx",
        err,
      );
      if (
        err.message.includes("popup-closed-by-user") ||
        err.message.includes("cancelled")
      ) {
        setError("Sign up was cancelled. Please try again.");
      } else {
        setError(
          err.message || "Failed to sign up with Apple. Please try again.",
        );
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full bg-white text-gray-800 flex flex-col p-6">
      <header className="w-full max-w-sm mx-auto">
        <button
          onClick={() => navigate("/welcome")}
          className="text-gray-400 hover:text-gray-700 p-2 -ml-2"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
      </header>

      <main className="flex-1 flex flex-col justify-center items-center">
        <div className="w-full max-w-sm mx-auto animate-fade-in-zoom">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Create Account
            </h1>
            <p className="text-gray-500 mt-2 leading-relaxed">
              Let's get you set up so you can start connecting.
            </p>
          </div>

          {/* Profile Photo Section */}
          <div className="mb-6 text-center">
            <div className="relative inline-block">
              <img
                src={getCurrentProfileImage()}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
              />
              <button
                type="button"
                onClick={handleAvatarPhotoClick}
                className="absolute -bottom-2 -right-2 bg-violet-600 text-white p-2 rounded-full hover:bg-violet-700 transition-colors shadow-lg"
              >
                <CameraIcon className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Profile photo (optional)
            </p>

            <div className="flex justify-center gap-2 mt-3">
              {profilePhotoUrl && (
                <button
                  type="button"
                  onClick={removePhoto}
                  className="text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  Remove Photo
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowAvatarSelection(!showAvatarSelection)}
                className="text-xs text-violet-600 hover:text-violet-700 font-medium"
              >
                {showAvatarSelection ? "Hide Avatars" : "Choose Avatar"}
              </button>
            </div>

            {/* Avatar Selection */}
            {showAvatarSelection && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-600 font-medium">
                    Choose an animated avatar:
                  </p>
                  <button
                    type="button"
                    onClick={refreshAvatarChoices}
                    className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                  >
                    Refresh
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {AVATAR_OPTIONS.map((avatar) => (
                    <button
                      key={avatar.style}
                      type="button"
                      onClick={() => selectAvatar(avatar.style)}
                      className={`relative p-1 rounded-lg transition-all group ${
                        selectedAvatar === avatar.style
                          ? "ring-2 ring-violet-500 bg-violet-50"
                          : "hover:bg-gray-100"
                      }`}
                      title={avatar.name}
                    >
                      <img
                        src={`https://api.dicebear.com/8.x/${avatar.style}/svg?seed=${encodeURIComponent(avatarPreviewSeed)}`}
                        alt={avatar.name}
                        className="w-12 h-12 rounded-lg"
                      />
                      {selectedAvatar === avatar.style && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-violet-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <AtSignIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                autoComplete="email"
                className="w-full bg-gray-100 border-gray-200 border rounded-full pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-800 placeholder-gray-500"
                required
              />
            </div>
            <div className="relative">
              <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="new-password"
                className="w-full bg-gray-100 border-gray-200 border rounded-full pl-12 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-800 placeholder-gray-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOffIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {error && (
              <p className="text-red-500 text-sm text-center font-semibold">
                {error}
              </p>
            )}

            {/* Terms and Conditions Checkbox */}
            <div className="flex items-start gap-3 py-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={isLoading}
                // disabled={!hasClickedTerms}
                className="mt-1 w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500 focus:ring-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              />
              <label
                htmlFor="terms"
                className="text-sm text-gray-600 leading-relaxed"
              >
                I accept the{" "}
                <a
                  href="https://whoisin.sg/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setHasClickedTerms(true)}
                  className="text-violet-600 hover:text-violet-700 font-semibold hover:underline"
                >
                  <u>Terms and Conditions</u>
                </a>
                {!hasClickedTerms && (
                  <span className="block text-xs text-gray-500 mt-1">
                    Please click to read the terms first
                  </span>
                )}
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-violet-600 text-white font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-violet-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-violet-500/30 disabled:hover:scale-100 disabled:cursor-not-allowed"
              disabled={isLoading || !acceptedTerms}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          <button
            onClick={handleGoogleSignUp}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 disabled:hover:scale-100 disabled:cursor-not-allowed"
            disabled={isLoading || !acceptedTerms}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? "Creating Account..." : "Continue with Google"}
          </button>

          {/* Apple Sign In is only available on iOS (native support) */}
          {platform === "ios" && (
            <button
              onClick={handleAppleSignUp}
              className="w-full mt-3 bg-black text-white font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-gray-900 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 disabled:hover:scale-100 disabled:cursor-not-allowed"
              disabled={isLoading || !acceptedTerms}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {isLoading ? "Creating Account..." : "Continue with Apple"}
            </button>
          )}

          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-bold text-violet-600 hover:underline"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </main>

      {isPhotoModalOpen && (
        <PhotoSelectionModal
          isOpen={isPhotoModalOpen}
          onClose={() => setIsPhotoModalOpen(false)}
          onSelect={(url) => {
            setProfilePhotoUrl(url);
            setIsPhotoModalOpen(false);
            setShowAvatarSelection(false);
          }}
          currentPhoto={profilePhotoUrl || getCurrentProfileImage()}
          allowMultiple={false}
        />
      )}
    </div>
  );
};

export default SignUpPage;
